const NAVIGATION_ELEMENT = 'rn-responsive-navigation';

if (!customElements.get(NAVIGATION_ELEMENT)) {
  customElements.define(
    NAVIGATION_ELEMENT,
    class RutaNaturalResponsiveNavigation extends HTMLElement {
      connectedCallback() {
        if (this._connected) return;

        this._connected = true;
        this._list = this.querySelector('[data-navigation-list]');
        this._items = this._list
          ? Array.from(this._list.querySelectorAll('[data-navigation-item]'))
          : [];

        if (!this._list || this._items.length === 0) return;

        this._queueFit = this._queueFit.bind(this);
        this._handleResize = this._handleResize.bind(this);
        this._handleFontLoad = this._queueFit;
        this._handleDrawerToggle = this._handleDrawerToggle.bind(this);

        if (this.dataset.placement === 'row') {
          this._drawerDetails = this.closest('#header-component')?.querySelector(
            '.menu-drawer-container',
          );
          this._drawerDetails?.addEventListener('toggle', this._handleDrawerToggle);
        }

        this._resizeObserver = new ResizeObserver(this._queueFit);
        this._resizeObserver.observe(this);
        window.addEventListener('resize', this._handleResize, { passive: true });
        document.fonts?.addEventListener('loadingdone', this._handleFontLoad);
        document.fonts?.addEventListener('loadingerror', this._handleFontLoad);
        document.fonts?.ready.then(() => {
          if (this._connected) this._queueFit();
        });

        this._queueFit();
      }

      disconnectedCallback() {
        this._connected = false;
        this._resizeObserver?.disconnect();
        this._resizeObserver = null;
        this._drawerDetails?.removeEventListener('toggle', this._handleDrawerToggle);
        this._drawerDetails = null;
        window.removeEventListener('resize', this._handleResize);
        document.fonts?.removeEventListener('loadingdone', this._handleFontLoad);
        document.fonts?.removeEventListener('loadingerror', this._handleFontLoad);
        if (this._frame) cancelAnimationFrame(this._frame);
        this._frame = null;
      }

      _queueFit() {
        if (!this._connected || this._frame) return;
        this._frame = requestAnimationFrame(() => {
          this._frame = null;
          this._applyFit();
        });
      }

      _handleResize() {
        this._closeDrawerIfHidden();

        const activeLink = this.contains(document.activeElement)
          ? document.activeElement.closest('[data-navigation-link]')
          : null;

        this._queueFit();

        if (!activeLink) return;
        requestAnimationFrame(() => {
          if (activeLink.getClientRects().length > 0) return;
          this._focusVisibleCounterpart(activeLink.dataset.navigationIndex);
        });
      }

      _handleDrawerToggle() {
        if (!this._drawerDetails) return;

        const trigger = this._drawerDetails.querySelector('summary.header__icon--menu');
        if (this._drawerDetails.open) {
          this._drawerReturnTarget = trigger;
          return;
        }

        const focusTarget = this._focusNavigationOnDrawerClose
          ? this._visibleNavigationFocusTarget()
          : this._drawerReturnTarget || trigger;
        this._focusNavigationOnDrawerClose = false;
        this._drawerReturnTarget = null;

        if (!focusTarget) return;
        requestAnimationFrame(() => {
          if (
            !this.isConnected ||
            this._drawerDetails?.open ||
            !focusTarget.isConnected ||
            !focusTarget.getClientRects().length
          ) {
            return;
          }
          focusTarget.focus({ preventScroll: true });
        });
      }

      _closeDrawerIfHidden() {
        if (!this._drawerDetails?.open || this._drawerDetails.getClientRects().length) return;

        const drawer = this._drawerDetails.closest('header-drawer');
        if (!drawer || typeof drawer.close !== 'function') return;

        this._focusNavigationOnDrawerClose = true;
        drawer.close();
      }

      _visibleNavigationFocusTarget() {
        const links = Array.from(
          this.closest('#header-component')?.querySelectorAll(
            'rn-responsive-navigation [data-navigation-link]',
          ) ?? [],
        ).filter((link) => link.getClientRects().length);

        return links.find((link) => link.getAttribute('aria-current') === 'page') || links[0];
      }

      _measureWidths() {
        const measurementList = this._list.cloneNode(true);
        measurementList.classList.add('rn-responsive-navigation__measure');
        measurementList.setAttribute('aria-hidden', 'true');
        measurementList.inert = true;
        measurementList.querySelectorAll('[data-navigation-item]').forEach((item) => {
          item.hidden = false;
        });

        this.dataset.measuring = 'true';
        this.append(measurementList);

        const itemWidths = Array.from(
          measurementList.querySelectorAll('[data-navigation-item]'),
        ).map((item) => item.getBoundingClientRect().width);
        const listStyle = getComputedStyle(measurementList);
        const gap = parseFloat(listStyle.columnGap) || 0;
        const padding =
          (parseFloat(listStyle.paddingInlineStart) || 0) +
          (parseFloat(listStyle.paddingInlineEnd) || 0);
        const available = Math.max(
          0,
          this.clientWidth -
            (parseFloat(getComputedStyle(this).paddingInlineStart) || 0) -
            (parseFloat(getComputedStyle(this).paddingInlineEnd) || 0),
        );

        measurementList.remove();
        delete this.dataset.measuring;

        return {
          itemWidths,
          gap,
          padding,
          available,
        };
      }

      _requiredWidth(indices, measurement) {
        return (
          indices.reduce((total, index) => total + measurement.itemWidths[index], 0) +
          measurement.gap * Math.max(0, indices.length - 1) +
          measurement.padding
        );
      }

      _applyFit() {
        if (!this.isConnected || this.getClientRects().length === 0) return;

        const measurement = this._measureWidths();
        const allIndices = this._items.map((_, index) => index);
        let visibleIndices = allIndices;

        if (this.dataset.placement === 'row') {
          const alimentosIndex = this._items.findIndex(
            (item) => item.dataset.fitHide === 'alimentos',
          );
          const suplementosIndex = this._items.findIndex(
            (item) => item.dataset.fitHide === 'suplementos',
          );

          if (
            alimentosIndex >= 0 &&
            this._requiredWidth(visibleIndices, measurement) > measurement.available
          ) {
            visibleIndices = visibleIndices.filter((index) => index !== alimentosIndex);
          }

          if (
            suplementosIndex >= 0 &&
            this._requiredWidth(visibleIndices, measurement) > measurement.available
          ) {
            visibleIndices = visibleIndices.filter((index) => index !== suplementosIndex);
          }
        }

        const activeLink = this.contains(document.activeElement)
          ? document.activeElement.closest('[data-navigation-link]')
          : null;

        this._items.forEach((item, index) => {
          item.hidden = !visibleIndices.includes(index);
        });
        this.dataset.visibleCount = String(visibleIndices.length);
        this.dataset.enhanced = 'true';

        if (
          activeLink &&
          !visibleIndices.includes(Number(activeLink.dataset.navigationIndex))
        ) {
          const activeIndex = Number(activeLink.dataset.navigationIndex);
          const nextIndex = visibleIndices.find((index) => index > activeIndex);
          const targetIndex = nextIndex ?? visibleIndices.at(-1);
          this._items[targetIndex]
            ?.querySelector('[data-navigation-link]')
            ?.focus({ preventScroll: true });
        }
      }

      _focusVisibleCounterpart(index) {
        if (index == null) return;
        const header = this.closest('#header-component');
        const selector =
          'rn-responsive-navigation [data-navigation-link][data-navigation-index="' +
          index +
          '"]';
        const counterparts = Array.from(header?.querySelectorAll(selector) ?? []);
        const target = counterparts.find((link) => link.getClientRects().length > 0);
        target?.focus({ preventScroll: true });
      }
    },
  );
}
